import { useFetcher } from '@remix-run/react';
import { Atom, useStore, WritableAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

interface AtomConfig {
  atoms?: Record<string, WritableAtom<any, any, any>>;
  atomFamilies?: Record<string, (...args: any[]) => WritableAtom<any, any, any>>;
}

interface IUseRouteLoaderResult {
  state: 'idle' | 'loading' | 'submitting';
  load: () => void;
}

const getAtom = (atom: AtomConfig, key: string): Atom<unknown> => {
  let res: Atom<unknown>;
  if (key.includes('+')) {
    // for atom family
    const [atomKey, ...args] = key.split('+');
    const decodeArgs = args.map((a) => decodeURIComponent(a));
    const atomFamily = (atom.atomFamilies || {})[atomKey];

    if (!atomFamily) {
      console.error('atomFamily not found for key: %s', key);
    }

    res = atomFamily(...decodeArgs);
  } else {
    // for plain atom
    res = (atom.atoms || {})[key];
  }

  if (process.env.NODE_ENV === 'development' && !res) {
    throw new Error(`no such atom for key: ${key}`);
  }

  return res;
};

export const useRouteLoader = (
  method: string,
  params: Record<string, string | number>,
  atom: AtomConfig,
): IUseRouteLoaderResult => {
  const store = useStore();
  const fetcher = useFetcher();

  const load = useCallback(() => {
    const searchParams = new URLSearchParams();
    searchParams.set('method', method);
    Object.entries(params).forEach(([key, value]) => {
      const str = String(value);
      if (str) {
        searchParams.set(key, String(value));
      }
    });
    fetcher.load(`${location.pathname}?${searchParams.toString()}`);
  }, [method, params]);

  useEffect(() => {
    const { data } = fetcher;

    if (!data) {
      return;
    }

    Object.entries(data).forEach(([key, value]) => {
      if (!key.startsWith('$')) {
        return;
      }
      const atomToSet: any = getAtom(atom, key);
      store.set(atomToSet, value);
    });
  }, [fetcher.data]);

  return {
    state: fetcher.state,
    load,
  };
};
